<script setup>
import { showModal, hideModal } from '@/Utils/modalBus'
import DynamicService from '@/services/DynamicService'
import { computed, ref, onMounted, getCurrentInstance, watch } from 'vue'
import { useNotification } from "@kyvg/vue3-notification"
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import _ from 'lodash'
import IntegrationService from '@/services/IntegrationService'
import ConfirmModal from '@/components/modals/ConfirmModal.vue'
import FormSwitch from '@/components/Atoms/FormSwitch.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import FormInput from '@/components/Atoms/FormInput.vue'
import FormText from '@/components/Atoms/FormText.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconInfo from '@/components/icon/IconInfo.vue'
import IconEye from '@/components/icon/IconEye.vue'
import IconEyeCrossed from '@/components/icon/IconEyeCrossed.vue'
import Tooltip from '@/components/Atoms/Tooltip.vue'
import Badge from '@/components/Badge.vue'
import Collapse from '@/components/Atoms/Collapse.vue'
import Spinner from '@/components/ui/Spinner.vue'
import Multiselect from 'vue-multiselect'

const { notify } = useNotification()
const store = useStore()
const instance = getCurrentInstance()
const root = instance.proxy.$root
const route = useRoute()
const router = useRouter()

const features = ref({
	countryBlock: {
		enabled: false,
		value: [],
		valueType: 'array'
	},
	countryWhitelists: {
		enabled: false,
		value: [],
		valueType: 'array'
	},
	domainWhitelists: {
		enabled: false,
		value: [],
		valueType: 'array'
	},
	embedPassword: {
		enabled: false,
		value: null,
		valueType: 'string'
	},
})

const featureProcessing = ref({
	embedPassword: false,
	countryBlock: false,
	countryWhitelists: false,
	domainWhitelists: false,
})

const featureDisabled = ref({
	embedPassword: true,
	countryBlock: true,
	countryWhitelists: true,
	domainWhitelists: true,
})

const countryWhitelistSelection = ref([])
const countryList = ref([])
const countryBlockSelection = ref([])
const countryListLoaded = ref(false)
const whitelistedDomains = ref([])
const whitelistedDomainsLoaded = ref(false)
const passwordVisibility = ref(false)

const COLLAPSE_KEYS = ['embedPassword', 'countryWhitelists', 'countryBlock', 'domainWhitelists']

const collapseOpen = ref({
	embedPassword: false,
	countryWhitelists: false,
	countryBlock: false,
	domainWhitelists: false,
})

const player = computed(() => store.getters['DynamicPlayers/dynamicPlayer'])
const dynamicPlayerFeatures = computed(() => store.getters['User/dynamicPlayerFeatures'])
const productsFeatures = computed(() => store.state.User.aggregatedDefinition)

const permissions = computed(() => ({
	countryBlock: !!dynamicPlayerFeatures.value?.['geo-block'],
	countryWhitelists: !!dynamicPlayerFeatures.value?.['geo-whitelist'],
	domainWhitelists: !!dynamicPlayerFeatures.value?.['domain-whitelist'],
	embedPassword: true,
}))

const isFeatureActive = (feature) => {
	if (feature === 'embedPassword') {
		return features.value.embedPassword.enabled && !!features.value.embedPassword.value
	}
	if (feature === 'countryBlock') {
		return features.value.countryBlock.enabled && features.value.countryBlock.value?.length > 0
	}
	if (feature === 'countryWhitelists') {
		return features.value.countryWhitelists.enabled && features.value.countryWhitelists.value?.length > 0
	}
	if (feature === 'domainWhitelists') {
		return features.value.domainWhitelists.enabled && features.value.domainWhitelists.value?.length > 0
	}
	return false
}

const syncFeaturesFromPlayer = () => {
	const metadata = player.value?.metadata || {}
	features.value.countryWhitelists.enabled = !!(metadata.countryWhitelists?.length > 0)
	features.value.countryWhitelists.value = metadata.countryWhitelists || []
	features.value.countryBlock.enabled = !!(metadata.countryBlock?.length > 0)
	features.value.countryBlock.value = metadata.countryBlock || []
	features.value.domainWhitelists.enabled = !!(metadata.domainWhitelists?.length > 0)
	features.value.domainWhitelists.value = metadata.domainWhitelists || []
	features.value.embedPassword.enabled = !!metadata.embedPassword
	features.value.embedPassword.value = metadata.embedPassword || null

	if (countryListLoaded.value) {
		countryBlockSelection.value = _.map(
			features.value.countryBlock.value,
			code => _.find(countryList.value, { code })
		).filter(Boolean)
		countryWhitelistSelection.value = _.map(
			features.value.countryWhitelists.value,
			code => _.find(countryList.value, { code })
		).filter(Boolean)
	}

	whitelistedDomains.value = _.join(
		_.filter(features.value.domainWhitelists.value, item => item !== 'https://castr.com'),
		'\r\n',
	)
	if (features.value.domainWhitelists.value.length === 1 && features.value.domainWhitelists.value[0] === 'https://castr.com') {
		features.value.domainWhitelists.enabled = false
	}
}

const syncCollapseOpen = () => {
	COLLAPSE_KEYS.forEach((key) => {
		collapseOpen.value[key] = isFeatureActive(key)
	})
}

COLLAPSE_KEYS.forEach((key) => {
	watch(() => isFeatureActive(key), (active) => {
		if (active) collapseOpen.value[key] = true
	})
})

watch(player, () => {
	syncFeaturesFromPlayer()
	syncCollapseOpen()
}, { deep: true })

onMounted(async () => {
	await store.dispatch('DynamicPlayers/getPlayer', route.params.playerId)
	countryList.value = await IntegrationService.getCountryList()
	countryListLoaded.value = true
	syncFeaturesFromPlayer()
	whitelistedDomainsLoaded.value = true
	syncCollapseOpen()
})

const toggleFeature = async (featureName) => {
	if (featureProcessing.value[featureName]) return

	const feature = features.value[featureName]
	if (!feature) return

	let mappedSecurityFeatName = featureName
	switch (featureName) {
		case 'countryBlock':
			mappedSecurityFeatName = 'securityFeaturesGeoBlock'
			break
		case 'countryWhitelists':
			mappedSecurityFeatName = 'securityFeaturesGeoWhitelist'
			break
		case 'domainWhitelists':
			mappedSecurityFeatName = 'securityFeaturesDomainWhitelist'
			break
	}

	const data = _.filter(store.state.User.subscriptions, (item) => item.category === 'live' && item.enabled)[0]
	const disabledGeoFeaturePackages = [
		'5c6e7405b408c1b41dd32f46',
		'5c6e7405b408c1b41dd32f47',
		'5c6e7406b408c1b41dd32f48',
		'5c6e7406b408c1b41dd32f49',
		'5ccd92f8152b7f25111c39cf',
		'6362114709950a6a642540c3',
		'6010063d03afd67aa9831cd4',
		'6361ecd609950a6a642540b7',
		'6018dbb703afd67aa9831ce3',
		'6361ecd609950a6a642540b8',
		'636dcc7ced167e4759926fd9',
		'6018dc7403afd67aa9831ce4',
		'6361ecd609950a6a642540b9',
		'602cc7a503afd67aa9831ced',
		'6361ecd609950a6a642540ba',
		'602cc91703afd67aa9831cf2',
		'6361ecd609950a6a642540bf',
		'602cc95903afd67aa9831cf3',
		'6361ecd609950a6a642540c0',
		'6434ffbd40771d569f1e98e6',
		'6435032f40771d569f1e98f2',
		'6435001640771d569f1e98e8',
		'6435035f40771d569f1e98f4',
		'6435007440771d569f1e98ea',
		'643503a640771d569f1e98f6',
	]

	if ((data && disabledGeoFeaturePackages.indexOf(data.package._id) !== -1
		&& (['countryBlock'].indexOf(featureName) !== -1)
		&& features.value.countryBlock.enabled) || (data && disabledGeoFeaturePackages.indexOf(data.package._id) !== -1
			&& (['countryWhitelists'].indexOf(featureName) !== -1)
			&& !features.value.countryBlock.enabled)
	) {
		showModal('feature-upgrade')
		setTimeout(() => {
			features.value[featureName].enabled = !features.value[featureName].enabled
		}, 500)
		return
	}

	const trialAndAIOEntryPackages = [
		'5ccd92f8152b7f25111c39cf',
		'6362114709950a6a642540c3',
		'6010063d03afd67aa9831cd4',
		'6361ecd609950a6a642540b7',
		'6018dbb703afd67aa9831ce3',
		'6361ecd609950a6a642540b8',
		'636dcc7ced167e4759926fd9',
		'6434ffbd40771d569f1e98e6',
		'6435032f40771d569f1e98f2',
		'6435001640771d569f1e98e8',
		'6435035f40771d569f1e98f4',
	]
	if (data && trialAndAIOEntryPackages.indexOf(data.package._id) !== -1
		&& (['domainWhitelists'].indexOf(featureName) !== -1)
	) {
		showModal('feature-upgrade')
		setTimeout(() => {
			features.value[featureName].enabled = !features.value[featureName].enabled
		}, 500)
		return
	}

	if (!productsFeatures.value || productsFeatures.value['live'][mappedSecurityFeatName]) {
		showModal('feature-upgrade')
		setTimeout(() => {
			features.value[featureName].enabled = !features.value[featureName].enabled
		}, 500)
		return
	}

	const nstate = feature.enabled
	if (feature.valueType === 'bool' || !nstate) {
		const value = feature.valueType === 'array' && !nstate ? [] : nstate
		await saveSetting(featureName, value)
	}
}

const saveGeoBlocking = async () => {
	await saveSetting('countryBlock', _.map(countryBlockSelection.value, 'code'))
}

const saveGeoWhitelisting = async () => {
	await saveSetting('countryWhitelists', _.map(countryWhitelistSelection.value, 'code'))
}

const saveWhitelistedDomains = async () => {
	let domains = _.split(whitelistedDomains.value, '\n')
		.map(d => d.trim())
		.filter(Boolean)
	const castrExist = domains.find((item) => item === 'https://castr.com')
	if (_.isEmpty(castrExist)) {
		domains = [...domains, 'https://castr.com']
	}
	if (!domains.length) return

	await saveSetting('domainWhitelists', domains)
}

const saveSetting = async (key, value) => {
	featureProcessing.value[key] = true
	try {
		await DynamicService.savePlayerMetadata(player.value._id, key, value)
		await DynamicService.clearPlayerMetadataCache(player.value._id, player.value.key)
		await store.dispatch('DynamicPlayers/getPlayer', route.params.playerId)
		notify({ group: 'success', text: 'The settings changed successfully' })
	} catch (e) {
		notify({ group: 'error', text: 'Unable to save your changes.' })
	}
	featureProcessing.value[key] = false
	featureDisabled.value[key] = true
}

const setStreamPassword = async () => {
	const password = features.value.embedPassword.value
	if (!password) return
	await saveSetting('embedPassword', password)
}

const navigateToPayments = () => {
	router.push({ name: 'Payments', query: { category: 'live' } })
}

const isDisabled = (option) => featureProcessing.value[option] || featureDisabled.value[option]

const enableSave = (feature) => {
	featureDisabled.value[feature] = false
}

const togglePasswordVisibility = () => {
	passwordVisibility.value = !passwordVisibility.value
}
</script>

<template>
	<aside class="bg-surface-2 rounded-xl shadow-[0_2px_6px_rgba(0,0,0,0.28)] p-4 md:p-5 !mb-4">
		<Collapse class="!mb-4" v-model:is-open="collapseOpen.embedPassword">
			<template #header>
				<h4 class="text-tiny font-medium">Password Protection</h4>
				<Tooltip wrapper-class="block" content="Set password to access the player" v-show="!collapseOpen.embedPassword">
					<icon-base class="shrink-0 !ml-2">
						<icon-info />
					</icon-base>
				</Tooltip>
				<Badge class="ml-auto" pill v-if="!collapseOpen.embedPassword"
					:type="features.embedPassword.enabled ? 'enabled' : 'main'">
					{{ features.embedPassword.enabled ? '• Enabled' : 'Disabled' }}
				</Badge>
			</template>
			<template #content>
				<div class="flex items-center justify-between gap-x-2 -mt-4 !mb-2">
					<p class="text-xs text-surface-8">Require a password to access this player</p>
					<FormSwitch class="ml-auto !block" @change="toggleFeature('embedPassword')"
						v-model="features.embedPassword.enabled" :disabled="featureProcessing.embedPassword" />
				</div>
				<div class="flex flex-col items-stretch gap-2 !mt-1 sm:flex-row sm:items-end" v-if="features.embedPassword.enabled">
					<div class="relative flex items-center w-full">
						<FormInput label="Password" :type="passwordVisibility ? 'text' : 'password'" input-classes="!bg-surface-4"
							v-model="features.embedPassword.value" @input="enableSave('embedPassword')" placeholder="Set Password" />
						<FormButton isIcon size="sm" type="link" class="absolute right-0 bottom-px" @click="togglePasswordVisibility">
							<icon-base class="shrink-0" fill="none">
								<icon-eye v-if="!passwordVisibility" />
								<icon-eye-crossed v-else />
							</icon-base>
						</FormButton>
					</div>
					<FormButton class="shrink-0 justify-center" :disabled="isDisabled('embedPassword')" @click="setStreamPassword">
						{{ featureProcessing.embedPassword ? 'Saving' : 'Save' }}
					</FormButton>
				</div>
			</template>
		</Collapse>

		<Collapse class="!mb-4" v-model:is-open="collapseOpen.countryWhitelists">
			<template #header>
				<h4 class="text-tiny font-medium">Whitelist Countries</h4>
				<Tooltip wrapper-class="block" content="Player will only be accessible in selected countries"
					v-show="!collapseOpen.countryWhitelists">
					<icon-base class="shrink-0 !ml-2">
						<icon-info />
					</icon-base>
				</Tooltip>
				<Badge class="ml-auto" pill v-if="!collapseOpen.countryWhitelists"
					:type="features.countryWhitelists.enabled ? 'enabled' : 'main'">
					{{ features.countryWhitelists.enabled ? '• Enabled' : 'Disabled' }}
				</Badge>
			</template>
			<template #content>
				<div class="flex items-center justify-between gap-x-2 -mt-4 !mb-2">
					<p class="text-xs text-surface-8">Player will only be accessible in selected countries</p>
					<Tooltip wrapper-class="block" :active="!permissions.countryWhitelists">
						<FormSwitch class="!block" v-model="features.countryWhitelists.enabled"
							:disabled="featureProcessing.countryWhitelists || !permissions.countryWhitelists"
							@change="toggleFeature('countryWhitelists')" />
						<template #content>
							Please <a class="underline" target="_blank" href="/app/subscribe">upgrade</a> subscription to use this
							feature.
						</template>
					</Tooltip>
				</div>
				<div class="flex flex-col items-stretch gap-2 !mt-1 sm:flex-row sm:items-center" v-if="features.countryWhitelists.enabled">
					<template v-if="countryListLoaded">
						<multiselect v-model="countryWhitelistSelection" :options="countryList" :multiple="true" :taggable="true"
							tag-placeholder="Unsupported region" placeholder="Search countries..." label="name" track-by="code"
							class="min-h-[2rem] !bg-surface-4" @select="enableSave('countryWhitelists')"
							@remove="enableSave('countryWhitelists')" />
						<FormButton class="shrink-0 justify-center" :disabled="isDisabled('countryWhitelists')" @click="saveGeoWhitelisting">
							{{ featureProcessing.countryWhitelists ? 'Saving' : 'Save' }}
						</FormButton>
					</template>
					<template v-else>
						<Spinner />
					</template>
				</div>
			</template>
		</Collapse>

		<Collapse class="!mb-4" v-model:is-open="collapseOpen.countryBlock">
			<template #header>
				<h4 class="text-tiny font-medium">Blacklist Countries</h4>
				<Tooltip wrapper-class="block" content="Player will not be accessible in selected countries"
					v-show="!collapseOpen.countryBlock">
					<icon-base class="shrink-0 !ml-2">
						<icon-info />
					</icon-base>
				</Tooltip>
				<Badge class="ml-auto" pill v-if="!collapseOpen.countryBlock"
					:type="features.countryBlock.enabled ? 'enabled' : 'main'">
					{{ features.countryBlock.enabled ? '• Enabled' : 'Disabled' }}
				</Badge>
			</template>
			<template #content>
				<div class="flex items-center justify-between gap-x-2 -mt-4 !mb-2">
					<p class="text-xs text-surface-8">Player will not be accessible in selected countries</p>
					<Tooltip wrapper-class="block" :active="!permissions.countryBlock">
						<FormSwitch class="!block" v-model="features.countryBlock.enabled"
							:disabled="featureProcessing.countryBlock || !permissions.countryBlock"
							@change="toggleFeature('countryBlock')" />
						<template #content>
							Please <a class="underline" target="_blank" href="/app/subscribe">upgrade</a> subscription to use this
							feature.
						</template>
					</Tooltip>
				</div>
				<div class="flex flex-col items-stretch gap-2 !mt-1 sm:flex-row sm:items-center" v-if="features.countryBlock.enabled">
					<template v-if="countryListLoaded">
						<multiselect v-model="countryBlockSelection" :options="countryList" :multiple="true" :taggable="true"
							tag-placeholder="Unsupported region" placeholder="Search countries..." label="name" track-by="code"
							class="min-h-[2rem] !bg-surface-4" @select="enableSave('countryBlock')" @remove="enableSave('countryBlock')" />
						<FormButton class="shrink-0 justify-center" :disabled="isDisabled('countryBlock')" @click="saveGeoBlocking">
							{{ featureProcessing.countryBlock ? 'Saving' : 'Save' }}
						</FormButton>
					</template>
					<template v-else>
						<Spinner />
					</template>
				</div>
			</template>
		</Collapse>

		<Collapse class="!mb-4" v-model:is-open="collapseOpen.domainWhitelists">
			<template #header>
				<h4 class="text-tiny font-medium">Whitelist Domains</h4>
				<Tooltip wrapper-class="block" content="Restrict this player to specific domains only"
					v-show="!collapseOpen.domainWhitelists">
					<icon-base class="shrink-0 !ml-2">
						<icon-info />
					</icon-base>
				</Tooltip>
				<Badge class="ml-auto" pill v-if="!collapseOpen.domainWhitelists"
					:type="features.domainWhitelists.enabled ? 'enabled' : 'main'">
					{{ features.domainWhitelists.enabled ? '• Enabled' : 'Disabled' }}
				</Badge>
			</template>
			<template #content>
				<div class="flex items-center justify-between gap-x-2 -mt-4 !mb-2">
					<p class="text-xs text-surface-8">Restrict this player to specific domains only</p>
					<Tooltip wrapper-class="block" :active="!permissions.domainWhitelists">
						<FormSwitch class="!block" v-model="features.domainWhitelists.enabled"
							:disabled="featureProcessing.domainWhitelists || !permissions.domainWhitelists"
							@change="toggleFeature('domainWhitelists')" />
						<template #content>
							Please <a class="underline" target="_blank" href="/app/subscribe">upgrade</a> subscription to use this
							feature.
						</template>
					</Tooltip>
				</div>
				<div class="items-center gap-x-2 !mt-1" v-if="features.domainWhitelists.enabled">
					<template v-if="whitelistedDomainsLoaded">
						<div class="flex flex-col items-stretch gap-2 sm:flex-row sm:items-start">
						<FormText v-model="whitelistedDomains" custom-classes="!bg-surface-4 !h-24"
							placeholder="e.g., yoursite.com" @input="enableSave('domainWhitelists')" />
							<FormButton class="shrink-0 justify-center" :disabled="isDisabled('domainWhitelists')" @click="saveWhitelistedDomains">
								{{ featureProcessing.domainWhitelists ? 'Saving' : 'Save' }}
							</FormButton>
						</div>
						<p class="mt-3 text-xs text-surface-8">Whitelisted Domains (one domain per line)</p>
						<p class="text-xs text-surface-8 !mb-2">Example:</p>
						<div class="flex flex-col gap-1 text-error text-xs">
							<code class="text-error">https://example.com</code>
							<code class="text-error">https://mysite.com</code>
							<code class="text-error">https://analytics-site.net</code>
						</div>
					</template>
					<template v-else>
						<Spinner />
					</template>
				</div>
			</template>
		</Collapse>
	</aside>

	<confirm-modal modal-id="feature-upgrade" ok-text="Upgrade Now" cancel-text="Later" @modal-confirm="navigateToPayments">
		<p class="mb-0 text-s-m">
			<a target="_blank" class="text-reset text-underline" href="/app/subscribe">Upgrade</a> to access this feature. Check
			<a target="_blank" class="text-reset text-underline text-nowrap" href="https://castr.com/pricing">Pricing page</a>
			to know more about eligible plan.
		</p>
	</confirm-modal>
</template>

<style scoped></style>
